import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createJob, presignUpload, uploadToS3 } from "../api";
import { track } from "../lib/analytics";
import { obviousExplicit } from "../lib/profanity";
import { useMe, useSession } from "../state";

const PROMPT_HINTS = [
  "gentle wind through the hair",
  "subtle smile, slow blink",
  "leaves rustling in the background",
  "soft camera push-in",
];

export function Submit() {
  const { token } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const { me, refresh } = useMe();
  const fileRef = useRef<HTMLInputElement>(null);

  const seed = (loc.state as { prompt?: string } | null) || {};
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string>(seed.prompt ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hasBalance = me ? me.free_credits + me.tokens > 0 : true;
  const willUseFree = me ? me.free_credits > 0 : false;

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) setFile(f);
  };

  const submit = async () => {
    if (!file || !prompt.trim()) return;
    const bad = obviousExplicit(prompt);
    if (bad) {
      setErr(`prompt contains content we don't allow (“${bad}”) — please rephrase`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const t = (await token())!;
      const { key, upload } = await presignUpload(t, file.type);
      await uploadToS3(upload, file);
      await createJob(t, key, prompt);
      track("job_submitted", { length: prompt.length });
      await refresh();
      nav("/jobs");
    } catch (e: any) {
      setErr(e.message || "something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 mt-6">
      <section>
        <h2 className="font-display font-black text-3xl mb-1">drop a photo</h2>
        <p className="text-ink-soft mb-4">jpg or png · up to 20 mb</p>

        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="block aspect-[4/5] rounded-3xl border-2 border-dashed border-ink/15 bg-cream-dark/40
                     hover:border-coral/50 hover:bg-cream-dark/70 transition cursor-pointer overflow-hidden"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {preview ? (
            <img src={preview} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 text-ink-soft">
              <span className="text-5xl mb-3 opacity-70">＋</span>
              <p className="font-medium text-ink">drop here, or click to choose</p>
              <p className="text-xs mt-2 opacity-70">your photo stays private to you</p>
            </div>
          )}
        </label>
      </section>

      <section className="flex flex-col">
        <h2 className="font-display font-black text-3xl mb-1">describe what should happen</h2>
        <p className="text-ink-soft mb-4">a sentence or two is plenty</p>

        <aside className="relative bg-amber/25 border border-amber/50 rounded-2xl px-4 py-3 mb-4 text-sm text-ink-soft">
          {/* speech-bubble tail pointing left, toward the dropzone */}
          <span className="hidden md:block absolute -left-2 top-5 w-3 h-3 bg-amber/25 border-l border-b border-amber/50 rotate-45" />
          <b className="text-ink">💡 tip:</b>{" "}
          for best quality use high-quality images and make sure all people are clearly visible.
        </aside>

        <textarea
          className="textarea h-40 resize-none"
          placeholder="e.g. she turns and smiles, soft breeze, golden-hour light"
          value={prompt}
          maxLength={2000}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="flex flex-wrap gap-1.5 mt-3">
          {PROMPT_HINTS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setPrompt((p) => (p ? `${p.replace(/\s+$/, "")}, ${h}` : h))}
              className="text-xs px-3 py-1 rounded-full bg-cream-dark text-ink-soft hover:text-ink hover:bg-amber/40 transition"
            >
              {h}
            </button>
          ))}
        </div>

        <div className="mt-auto pt-6 flex items-center gap-3 flex-wrap">
          {hasBalance ? (
            <button
              className="btn-primary text-base px-5 py-2.5"
              onClick={submit}
              disabled={busy || !file || !prompt.trim()}
            >
              {busy ? "uploading…" : "make it live"}
            </button>
          ) : (
            <Link to="/buy" className="btn-primary text-base px-5 py-2.5">
              out of credits — get tokens
            </Link>
          )}
          {err && <span className="text-sm text-coral-dark">{err}</span>}
        </div>
      </section>
    </div>
  );
}
